import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import {IconButton, Menu, Typography, SxProps, Grid, Chip} from '@mui/material';

import {EmpionActionMenuItems} from '../EmpionActionMenuItems/EmpionActionMenuItems';
import {EmpionTooltip} from '../EmpionTooltip/EmpionTooltip';

import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../hooks/useApiWrite';
import {greenColors, orangeColors} from '../../layouts/theme/designSystem';
import {Delete} from '../../lib/api/DELETE/Delete';
import {DeleteRoutes} from '../../lib/api/DELETE/DeleteRoutes';
import {Post} from '../../lib/api/POST/Post';
import {PostRoutes} from '../../lib/api/POST/PostRoutes';
import {SUPPORTED_TAGS} from '../../lib/constants';
import {MatchData, MatchTagResponse, SupportedTag, SupportedTagBE} from '../../types/shared-recruiting-types';

interface TagsProps {
  companyId?: string;
  match: MatchData;
  onDeleteTagCallback?: (tag: SupportedTagBE) => void;
  onAddTagCallback?: (tag: SupportedTagBE) => void;
  isReadOnly?: boolean;
}

export default function MatchTags({match, companyId, onDeleteTagCallback, onAddTagCallback, isReadOnly}: TagsProps) {
  const [itemTags, setItemTags] = useState<SupportedTag[]>([]);
  const [optionTags, setOptions] = useState<SupportedTag[]>([]);
  const [resourceId, setResourceId] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<SupportedTag | null>(null);
  const [trigger, setTrigger] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);
  const currentLanguage = useContext(LanguageContext);

  const MenuStyles: SxProps = {
    backgroundImage: 'none',
    '.MuiMenu-paper': {
      backgroundColor: orangeColors[4],
      borderRadius: '10px 0px 10px 10px',
      padding: '16px 48px 16px 0px',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.06)',
      backgroundImage: 'none',
      width: 'fit-content',
    },
  };

  useEffect(() => {
    if (match.tags) {
      const itemTags = match.tags
        .map((tag) => {
          const supported = SUPPORTED_TAGS.find((supportedTag) => supportedTag.id === tag.tagLabel);
          const newTag = supported ? {...supported, resourceId: tag.resourceId} : null;
          return newTag;
        })
        .filter((tag) => tag !== null) as SupportedTag[];

      setItemTags(itemTags);

      const options = SUPPORTED_TAGS.filter((tag) => !itemTags.some((itemTag) => itemTag.id === tag.id));
      setOptions(options);
    }
  }, [match.tags]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const transformTag = (data: MatchTagResponse) => {
    return {
      id: data.matchTag.tagLabel,
      label: {
        en: SUPPORTED_TAGS.find((tag) => tag.id === data.matchTag.tagLabel)?.label.en || '',
        de: SUPPORTED_TAGS.find((tag) => tag.id === data.matchTag.tagLabel)?.label.de || '',
      },
      resourceId: data.matchTag.resourceId,
    };
  };

  const {
    makeRequest: addTag,
    isLoading: isAddTagLoading,
    error: isAddTagError,
  } = useApiWrite<Post.AddMatchTags, MatchTagResponse>({
    method: 'post',
    path: PostRoutes[Post.AddMatchTags](companyId || '', match.resourceId),
    onSuccess: (data: MatchTagResponse) => {
      if (data) {
        if (onAddTagCallback) {
          onAddTagCallback(data.matchTag);
        }
        setResourceId(data.matchTag.resourceId);
        setSelectedTag(transformTag(data));
        setTrigger(false);
      }
    },
  });

  const {
    makeRequest: deleteTag,
    isLoading: isDeleteTagLoading,
    error: isDeleteTagError,
  } = useApiWrite<Delete.DeleteTag>({
    method: 'delete',
    path: DeleteRoutes[Delete.DeleteTag](companyId ?? '', match.resourceId, resourceId),
    onSuccess: () => {
      if (onDeleteTagCallback) {
        onDeleteTagCallback({resourceId: resourceId, tagLabel: selectedTag?.id as string});
      }
    },
  });

  useEffect(() => {
    if (isAddTagError || isDeleteTagError) {
      dispatchMessage(t('shared.errors.errorAddingTags'), 'error');
    }
  }, [isAddTagError, dispatchMessage, t, isDeleteTagError]);

  useEffect(() => {
    if (resourceId && selectedTag && trigger) {
      const body = {
        matchTag: {
          tagLabel: selectedTag.id,
        },
      };

      deleteTag(body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, selectedTag, trigger]);

  const renderMenuItemsOptions = () => {
    return optionTags.map((tag) => {
      return {
        key: tag.id,
        label: tag.label[currentLanguage],

        onClick: () => {
          if (isAddTagLoading) return;

          const body = {
            matchTag: {
              tagLabel: tag.id,
            },
          };
          addTag(body);
        },
      };
    });
  };

  const handleDelete = (tag: SupportedTag) => {
    if (isReadOnly || isDeleteTagLoading) return;
    setResourceId(tag.resourceId as string);
    setSelectedTag(tag);
    setTrigger(true);
  };

  useEffect(() => {
    if (optionTags.length === 0) {
      setAnchorEl(null);
    }
  }, [optionTags]);

  const renderSelectedTags = () => {
    if (itemTags && itemTags.length > 0) {
      return itemTags.map((tag) => {
        return (
          <Grid item key={tag.resourceId} sx={{marginRight: {xs: '6px', md: '12px'}}}>
            <Chip
              label={tag.label[currentLanguage]}
              sx={{
                backgroundColor: greenColors[70],
                color: greenColors[2],
                borderRadius: '4px',
                height: '30px',
                mb: '8px',
              }}
              // If readOnly, don't show the delete icon button
              onDelete={isReadOnly ? undefined : () => handleDelete(tag)}
            />
          </Grid>
        );
      });
    }
  };

  return (
    <Grid
      item
      container
      sx={{
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {renderSelectedTags()}

      {/* if readOnly hide */}
      {!isReadOnly && (
        <EmpionTooltip isEnabled={optionTags.length === 0} title={t('companyInbox.tags.noMoreTags')}>
          <span>
            <IconButton
              onClick={handleClick}
              sx={{
                borderRadius: open ? '4px 4px 0px 0px' : '4px',
                padding: '0px 8px 0px 8px',
                height: '30px',
                width: itemTags && itemTags.length == 0 ? 'auto' : '32px',
                color: open ? greenColors[500] : greenColors[40],
                backgroundColor: open ? orangeColors[4] : greenColors[500],
                '&:hover': {
                  backgroundColor: greenColors[40],
                  color: greenColors[900],
                },
                boxShadow: 'none',
              }}
              disabled={optionTags.length === 0}
            >
              <AddIcon />
              {itemTags && itemTags.length == 0 && (
                <Typography
                  variant='body1'
                  sx={{
                    color: greenColors[40],
                    '&:hover': {
                      color: greenColors[900],
                    },
                  }}
                >
                  {t('companyInbox.tags.addTag')}
                </Typography>
              )}
            </IconButton>
          </span>
        </EmpionTooltip>
      )}
      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={MenuStyles}
      >
        <EmpionActionMenuItems
          variant='body1'
          menuItems={renderMenuItemsOptions() as any}
          sx={{
            mr: '16px',
            ml: '26px',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: 'background.menu',
              width: '100%',
            },
          }}
        />
      </Menu>
    </Grid>
  );
}
